import * as React from "react";
import styled, { css } from "styled-components";
import { TextProps, TextUppercase } from "../../elements/Text";
import { select } from "@hubper/theme";

import {
  inputPlaceholderStyle,
  inputResetStyle
} from "../../elements/TextInput";

const focusStyle = css<{ error?: boolean }>`
  border: 1px solid
    ${p =>
      p.error ? select.color(["error", 4], p) : select.color(["grey", 3], p)};
  background-color: white;
`;

const uppercaseStyle = css<TextUppercase>`
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const errorStyle = css`
  border: 1px solid ${select.color(["error", 4])};
`;

const Input = styled.input<TextProps & { error?: boolean }>`
  ${inputPlaceholderStyle}
  ${inputResetStyle}

  &:focus {
    ${focusStyle}
  }

  &:hover:not(:focus) {
    background-color: ${p =>
      p.error ? "white" : select.color(["grey", 0], p)};
  }

  position: relative;
  border: 1px solid transparent;
  box-sizing: border-box;
  display: inline-block;
  color: ${select.findColor};
  font-family: ${select.findFont};
  font-weight: ${select.findFontWeight};
  font-size: ${select.findFontSize};
  line-height: ${select.findLineHeight};

  ${p => p.error && errorStyle}

  &:not(:focus) {
    cursor: pointer;
  }

  &:focus {
    cursor: default;
  }

  border-radius: ${select.radius("light")};
  transition: border-color ${select.speed("fast")};
  padding-left: ${select.space(2)};
  padding-right: ${select.space(2)};
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  ${p => p.uppercase && uppercaseStyle}

  & + span {
    color: ${select.color(["error", 4])};
    text-align: end;
    padding-top: 2px;
    padding-bottom: 4px;
    font-family: ${select.font("Lato")};
    font-size: ${select.fontSize(-2)};
  }
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  textProps: TextProps;
  value?: string;
  error?: boolean | string;
} & React.HTMLAttributes<HTMLInputElement>;

const defaultTextProps = {
  f: "Lato",
  fw: "inherit",
  fs: 0,
  c: ["grey", 9],
  lh: "normal"
};

function PreviewInput({ textProps, ...props }: Props, ref: any) {
  const x: any = { ...defaultTextProps, ...textProps };

  return (
    <>
      <Input ref={ref} {...props} {...x} />
      {props.error && typeof props.error === "string" && (
        <span style={{ width: (props.style && props.style.width) || "auto" }}>
          {props.error}
        </span>
      )}
    </>
  );
}

export default React.forwardRef(PreviewInput);
